import { Tooltip } from '@mui/material';
import React from 'react';
import { connect } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { translate } from '../../../base/i18n/functions';
import Message from '../../../base/react/components/web/Message.web';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import { getCanReplyToMessage, getFormattedTimestamp, getMessageText, getPrivateNoticeMessage } from '../../functions';
import PrivateMessageButton from './PrivateMessageButton';
import { MESSAGE_TYPE_LOCAL, MESSAGE_TYPE_REMOTE } from '../../constants';
import Icon from '../../../base/icons/components/Icon';
import { removeFileUpload, setPendingMessage } from '../../../vtfileupload-moderation/slice';
import { IconClose, IconForward } from '../../../base/icons/svg';
import './style.css';
import { Trans } from 'react-i18next';
const useStyles = makeStyles()((theme) => {
    return {
        chatMessageWrapper: {
            maxWidth: '100%'
        },
        chatMessage: {
            display: 'inline-flex',
            // padding: '6px',
            // paddingLeft: '12px',
            backgroundColor: theme.palette.ui02,
            borderRadius: '4px 12px 12px 12px',
            maxWidth: '100%',
            marginTop: '4px',
            boxSizing: 'border-box',
            // '&.privatemessage': {
            //     backgroundColor: theme.palette.support05
            // },
            '&.local': {
                backgroundColor: theme.palette.ui04,
                borderRadius: '12px 4px 12px 12px',
                // '&.privatemessage': {
                //     backgroundColor: theme.palette.support05
                // }
            },
            '&.error': {
                backgroundColor: theme.palette.actionDanger,
                borderRadius: 0,
                fontWeight: 100
            },
            '&.lobbymessage': {
                backgroundColor: theme.palette.support05
            }
        },
        replyWrapper: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            maxWidth: '100%'
        },
        messageContent: {
            maxWidth: '100%',
            overflow: 'hidden',
            flex: 1
        },
        replyButtonContainer: {
            display: 'flex',
            alignItems: 'flex-start',
            height: '100%'
        },
        replyButton: {
            padding: '2px'
        },
        displayName: {
            ...withPixelLineHeight(theme.typography.labelBold),
            color: theme.palette.text02,
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            marginBottom: theme.spacing(1)
        },
        userMessage: {
            ...withPixelLineHeight(theme.typography.bodyShortRegular),
            color: theme.palette.text01,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
        },
        privateMessageNotice: {
            ...withPixelLineHeight(theme.typography.labelRegular),
            color: theme.palette.text02,
            marginTop: theme.spacing(1)
        },
        timestamp: {
            ...withPixelLineHeight(theme.typography.labelRegular),
            color: theme.palette.text03,
            marginTop: theme.spacing(1),
            minWidth: "fit-content"
        },
        messageActionIcon: {
            '& svg': {
                fill: '#606060 !important'
            }
        }
    };
});
/**
 * Renders a single chat message.
 *
 * @param {IProps} props - Component's props.
 * @returns {JSX}
 */
const ChatMessage = ({ canReply, knocking, message, showDisplayName, showTimestamp, indexClass, type, t, dispatch, displayName }) => {
    const { classes, cx } = useStyles();
    const { attachments, messageType } = message;
    const isErrorPending = message.isPendingMessage ? message.attachments?.some(f => f.status === "error") : false;
    function _renderError() {
        return (React.createElement("div", { className: 'sender-container', style: { flexDirection: "column" } },
            React.createElement("div", { className: `timestamp error` }, t(`chat.errors.cantSend`)),
            React.createElement("div", { className: `timestamp error` }, t(`chat.errors.moveToSend`))));
    }
    function _removePrefixPhong(text) {
        if (text?.startsWith('Phòng ')) {
            return text.replace('Phòng ', '');
        }
        if (text?.startsWith('phòng ')) {
            return text.replace('phòng ', '');
        }
        return text;
    }
    /**
     * Renders the display name of the sender.
     *
     * @returns {React$Element<*>}
     */
    function _renderDisplayName() {
        return (React.createElement("div", { className: 'sender-container' },
            message.messageType === MESSAGE_TYPE_LOCAL && (React.createElement("div", { className: 'timestamp local' }, getFormattedTimestamp(message))),
            message.originSenderAccName ? (React.createElement("div", { className: 'display-name-group' },
                React.createElement(Tooltip, { title: `${message.originSenderAccName} | ${message.originCascadeName ? message.originCascadeName : message.senderAccName}`, placement: 'top' },
                    React.createElement("div", { "aria-hidden": true, className: 'main-display' },
                        React.createElement(Trans, { i18nKey: "chat.displayNameGroup", values: { name: message.originSenderAccName, originalRoom: _removePrefixPhong(message.originCascadeName ? message.originCascadeName : message.senderAccName) } }))),
                message.originCascadeName && (React.createElement("div", { className: 'forward-by' },
                    React.createElement(Icon, { src: IconForward }),
                    React.createElement("div", null,
                        React.createElement(Trans, { i18nKey: "chat.forwardBy", values: { forwardRoom: _removePrefixPhong(message.senderAccName) } })))))) : (React.createElement(Tooltip, { title: displayName || message.displayName, placement: 'top' },
                React.createElement("div", { "aria-hidden": true, className: 'display-name' }, displayName || message.displayName))),
            (message.privateMessage || message.messageType === MESSAGE_TYPE_LOCAL) && (React.createElement(React.Fragment, null,
                message.messageType === MESSAGE_TYPE_LOCAL && (React.createElement("div", { className: 'me-send' }, t('chat.me'))),
                React.createElement("div", { className: 'send-to' }, t('chat.to')),
                React.createElement(Tooltip, { title: message.messageType === MESSAGE_TYPE_REMOTE ? (t('chat.me')) : message.privateMessage ?
                        message.recipient :
                        t('chat.sendAll'), placement: 'top' },
                    React.createElement("div", { className: 'send-me' }, message.messageType === MESSAGE_TYPE_REMOTE ? (t('chat.me')) : message.privateMessage ?
                        message.recipient :
                        t('chat.sendAll'))))),
            message.messageType === MESSAGE_TYPE_REMOTE && (React.createElement("div", { className: 'timestamp remote' }, getFormattedTimestamp(message)))));
    }
    /**
     * Renders the message privacy notice.
     *
     * @returns {React$Element<*>}
     */
    function _renderPrivateNotice() {
        return (React.createElement("div", { className: classes.privateMessageNotice }, getPrivateNoticeMessage(message)));
    }
    /**
     * Renders the time at which the message was sent.
     *
     * @returns {React$Element<*>}
     */
    function _renderTimestamp() {
        return (React.createElement("div", { className: cx('timestamp', classes.timestamp) }, getFormattedTimestamp(message)));
    }
    return (React.createElement(React.Fragment, null,
        showDisplayName && _renderDisplayName(),
        React.createElement("div", { className: cx(classes.chatMessageWrapper, type, 'chatmessage-wrapper-container'), id: message.messageId, tabIndex: -1 },
            React.createElement("div", { className: `chatmessage-wrapper ${indexClass}`, tabIndex: -1 },
                message.messageType === MESSAGE_TYPE_LOCAL && message.isPendingMessage && (React.createElement("div", { className: 'messageactions' },
                    React.createElement(Tooltip, { title: t('chat.cancelMessage'), placement: "top" },
                        React.createElement(Icon, { className: cx(classes.messageActionIcon), size: 16, onClick: () => {
                                const attachmentIds = message.attachments?.map(ma => ma.id);
                                dispatch(removeFileUpload(attachmentIds || []));
                                dispatch(setPendingMessage(null));
                            }, src: IconClose })))),
                React.createElement("div", { className: cx('chatmessage', classes.chatMessage, type, message.privateMessage && 'privatemessage', message.lobbyChat && !knocking && 'lobbymessage', isErrorPending && 'error') },
                    React.createElement("div", { className: classes.replyWrapper },
                        React.createElement("div", { className: cx('messagecontent', classes.messageContent) },
                            React.createElement("div", { className: cx('usermessage', classes.userMessage) },
                                React.createElement(Message, { text: getMessageText(message), attachments: attachments, type: messageType })),
                            ((message.lobbyChat && !knocking))
                                && _renderPrivateNotice()))),
                message.messageType !== MESSAGE_TYPE_LOCAL && !message.originSenderAccName
                    && (React.createElement("div", { className: 'messageactions' },
                        React.createElement(PrivateMessageButton, { isLobbyMessage: message.lobbyChat, participantID: message.participantId })))),
            isErrorPending && _renderError())));
};
/**
 * Maps part of the Redux store to the props of this component.
 *
 * @param {Object} state - The Redux state.
 * @returns {IProps}
 */
function _mapStateToProps(state, { message }) {
    const { knocking } = state['features/lobby'];
    return {
        canReply: getCanReplyToMessage(state, message),
        knocking
    };
}
export default translate(connect(_mapStateToProps)(ChatMessage));
